'use client';

import { c as _c } from "react/compiler-runtime";
import { chartIndexCrosshairPlugin } from '@/domains/pages/shared/chart-crosshair';
import { TIME_SERIES_INTERACTION } from '@/domains/pages/shared/chart-interaction';
import { positionChartExternalTooltip } from '@/domains/pages/shared/chart-external-tooltip';
import { buildOrgOnlyOverTimeTooltipHtml, buildOverTimeTooltipHtml } from '@/domains/pages/ai-proficiency/components/chart-tooltip';
import type { TrendBenchmarkPoint } from '@/domains/pages/ai-proficiency/fixtures/benchmarks';
import { AIFI_CHART_PRIMARY_COLOR, AIFI_CHART_SECONDARY_COLOR, AIFI_LINE_CHART_OPTIONS } from '@/domains/pages/ai-proficiency/constants';
import { computePaddedYAxisBounds } from '@/domains/pages/shared/chart-axis';
import { ringHoverPoint, REPORT_LINE_SECONDARY_POINT_HOVER_RADIUS, smallSolidHoverPoint, orgLinePoints } from '@/domains/pages/ai-proficiency/utils/chart-hover-points';
import { formatOverTimeTooltipMonth } from '@/domains/pages/shared/trend-query';
import { themeColors } from '@/theme/palette';
import { AIFI_CHART_TICK_FONT } from '@/domains/pages/ai-proficiency/typography';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Chart as ChartJS, ChartOptions, Filler, LineController, LineElement, PointElement, LinearScale, CategoryScale } from 'chart.js';
import { useMemo, useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartNoDataEmptyState } from '@/comps/charts/analytics-chart/ChartNoDataEmptyState';
ChartJS.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, chartIndexCrosshairPlugin);
type ProficiencyOverTimeChartProps = {
  labels: string[];
  orgScores: Array<number | null>;
  benchmarks: TrendBenchmarkPoint[];
};
const ORG_COLOR = AIFI_CHART_PRIMARY_COLOR;
const BENCHMARK_COLOR = AIFI_CHART_SECONDARY_COLOR;
/** Same-hue translucent tint of the benchmark line, matching the app-wide "bright line, faint fill" chart style. */
const BAND_COLOR = `${BENCHMARK_COLOR}1A`;
const BAND_EDGE_COLOR = BENCHMARK_COLOR;
const HIDDEN_DATALABELS = {
  datalabels: {
    display: false
  }
};
const OVER_TIME_TOOLTIP_ID = 'ai-proficiency-over-time-tooltip';
function collectOverTimeYValues(orgScores: Array<number | null>, benchmarks: TrendBenchmarkPoint[]): number[] {
  return [...orgScores.filter((score): score is number => score != null), ...benchmarks.flatMap(point => [point.median, point.percentile25, point.percentile75])];
}
export function ProficiencyOverTimeChart(t0) {
  const $ = _c(41);
  const {
    labels,
    orgScores,
    benchmarks
  } = t0;
  const chartRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp2, t1);
  const hasData = labels.length > 0 && orgScores.some(_temp3);
  const hasBenchmarks = benchmarks.length > 0;
  let t2;
  if ($[1] !== benchmarks || $[2] !== orgScores) {
    t2 = computePaddedYAxisBounds(collectOverTimeYValues(orgScores, benchmarks));
    $[1] = benchmarks;
    $[2] = orgScores;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const yAxisBounds = t2;
  let t3;
  if ($[4] !== benchmarks || $[5] !== hasBenchmarks) {
    t3 = hasBenchmarks ? [{
      label: "25th percentile",
      data: benchmarks.map(_temp4),
      borderColor: "transparent",
      backgroundColor: BAND_COLOR,
      fill: "+1",
      tension: 0.35,
      order: 4,
      ...smallSolidHoverPoint(BAND_EDGE_COLOR)
    }, {
      label: "75th percentile",
      data: benchmarks.map(_temp5),
      borderColor: "transparent",
      backgroundColor: BAND_COLOR,
      fill: false,
      tension: 0.35,
      order: 3,
      ...smallSolidHoverPoint(BAND_EDGE_COLOR)
    }, {
      label: "Benchmark median",
      data: benchmarks.map(_temp6),
      borderColor: BENCHMARK_COLOR,
      borderDash: [4, 4],
      borderWidth: 1.5,
      fill: false,
      tension: 0.35,
      order: 2,
      ...ringHoverPoint(BENCHMARK_COLOR, REPORT_LINE_SECONDARY_POINT_HOVER_RADIUS)
    }] : [];
    $[4] = benchmarks;
    $[5] = hasBenchmarks;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const benchmarkDatasets = t3;
  let t4;
  if ($[7] !== orgScores) {
    t4 = orgLinePoints(ORG_COLOR, orgScores);
    $[7] = orgScores;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== orgScores || $[10] !== t4) {
    t5 = {
      label: "Org",
      data: orgScores,
      borderColor: ORG_COLOR,
      borderWidth: 2,
      fill: false,
      tension: 0.35,
      spanGaps: false,
      order: 1,
      ...t4
    };
    $[9] = orgScores;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== benchmarkDatasets || $[13] !== t5) {
    t6 = [...benchmarkDatasets, t5];
    $[12] = benchmarkDatasets;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== labels || $[16] !== t6) {
    t7 = {
      labels,
      datasets: t6
    };
    $[15] = labels;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const chartData = t7;
  let t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      padding: {
        top: 4,
        right: 8,
        left: 0,
        bottom: 0
      }
    };
    t9 = {
      grid: {
        display: false
      },
      border: {
        display: false
      },
      ticks: {
        color: themeColors.text.secondary,
        font: AIFI_CHART_TICK_FONT,
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 12
      }
    };
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      display: true,
      color: "rgba(255, 255, 255, 0.12)"
    };
    t11 = {
      color: "rgba(255, 255, 255, 0.08)",
      tickLength: 4
    };
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  let t12;
  if ($[22] !== yAxisBounds.stepSize) {
    t12 = {
      display: true,
      color: themeColors.text.secondary,
      font: AIFI_CHART_TICK_FONT,
      padding: 6,
      stepSize: yAxisBounds.stepSize,
      callback: _temp7
    };
    $[22] = yAxisBounds.stepSize;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== yAxisBounds.max || $[26] !== yAxisBounds.min) {
    t13 = {
      x: t9,
      y: {
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        border: t10,
        grid: t11,
        ticks: t12
      }
    };
    $[24] = t12;
    $[25] = yAxisBounds.max;
    $[26] = yAxisBounds.min;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      display: false
    };
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== benchmarks || $[30] !== hasBenchmarks || $[31] !== labels || $[32] !== orgScores) {
    t15 = {
      ...HIDDEN_DATALABELS,
      legend: t14,
      tooltip: {
        enabled: false,
        external(context) {
          let tooltipEl = document.getElementById(OVER_TIME_TOOLTIP_ID);
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = OVER_TIME_TOOLTIP_ID;
          }
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }
          const index = tooltipModel.dataPoints?.[0]?.dataIndex ?? 0;
          const orgScore = orgScores[index] ?? null;
          const benchmark = benchmarks[index];
          const bucketLabel = labels[index] ?? "";
          const title = formatOverTimeTooltipMonth(bucketLabel);
          tooltipEl.innerHTML = hasBenchmarks && benchmark ? buildOverTimeTooltipHtml({
            header: title,
            orgScore,
            benchmarkMedian: benchmark.median
          }) : buildOrgOnlyOverTimeTooltipHtml({
            header: title,
            orgScore
          });
          positionChartExternalTooltip(context.chart, tooltipEl, tooltipModel);
        }
      }
    };
    $[29] = benchmarks;
    $[30] = hasBenchmarks;
    $[31] = labels;
    $[32] = orgScores;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t13 || $[35] !== t15) {
    t16 = {
      ...AIFI_LINE_CHART_OPTIONS,
      responsive: true,
      maintainAspectRatio: false,
      interaction: TIME_SERIES_INTERACTION,
      hover: TIME_SERIES_INTERACTION,
      layout: t8,
      scales: t13,
      plugins: t15
    };
    $[34] = t13;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const options = t16;
  if (!hasData) {
    let t17;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <ChartNoDataEmptyState />;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    return t17;
  }
  let t17;
  if ($[38] !== chartData || $[39] !== options) {
    t17 = <div className="relative h-full min-h-55 w-full overflow-hidden" data-testid="proficiency-over-time-chart"><Line ref={chartRef} data={chartData} options={options} /></div>;
    $[38] = chartData;
    $[39] = options;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}
function _temp7(value) {
  return formatScore(Number(value), {
    decimals: 0
  });
}
function _temp6(point_1) {
  return point_1.median;
}
function _temp5(point_0) {
  return point_0.percentile75;
}
function _temp4(point) {
  return point.percentile25;
}
function _temp3(score) {
  return score != null;
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.getElementById(OVER_TIME_TOOLTIP_ID)?.remove();
}
type ProficiencyOverTimeLegendProps = {
  className?: string;
  showBenchmarks?: boolean;
};
export function ProficiencyOverTimeLegend(t0) {
  const $ = _c(8);
  const {
    className,
    showBenchmarks: t1
  } = t0;
  const showBenchmarks = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex flex-wrap items-center gap-4 text-xs text-muted-foreground", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="flex items-center gap-2"><span className="h-0.5 w-4" style={{
        backgroundColor: ORG_COLOR
      }} />Org AI proficiency</span>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== showBenchmarks) {
    t4 = showBenchmarks ? <><span className="flex items-center gap-2"><span className="h-0.5 w-4 border-t border-dashed" style={{
          borderColor: BENCHMARK_COLOR
        }} />Benchmark median</span><span className="flex items-center gap-2"><span className="size-2 rounded-sm" style={{
          backgroundColor: BAND_EDGE_COLOR
        }} />25th–75th percentile</span></> : null;
    $[3] = showBenchmarks;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}